<template>
  <div
    v-if="visible"
    class="help-assistant"
    :class="{ 'ha-dark': dark, 'ha-dragging': fab.dragging }"
    :style="rootStyle"
  >
    <!-- Chat panel -->
    <transition name="ha-pop">
      <v-card v-show="open" class="ha-panel" elevation="12" :style="panelStyle">
        <div class="ha-head">
          <v-avatar size="34" class="ha-head-av">
            <span class="ha-ai-ico ha-ai-ico--sm" v-html="aiIcon"></span>
          </v-avatar>
          <div class="ha-head-txt">
            <div class="ha-title">{{ assistantName }}</div>
            <div class="ha-sub">Ask about clients, deposits, KYC, pending tasks…</div>
          </div>
          <v-spacer />
          <v-btn icon small @click="resetChat" title="New chat">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon small @click="open = false" title="Minimise">
            <v-icon small>mdi-chevron-down</v-icon>
          </v-btn>
        </div>

        <div ref="scroll" class="ha-body">
          <div
            v-for="(m, i) in messages"
            :key="i"
            class="ha-msg"
            :class="m.role === 'user' ? 'ha-msg-user' : 'ha-msg-bot'"
          >
            <div class="ha-bubble">
              <div class="ha-text" v-html="render(m.text)" @click="onTextClick"></div>

              <!-- pending tasks card -->
              <div v-if="m.card && m.card.card_type === 'pending_tasks'" class="ha-card">
                <div
                  v-for="it in m.card.items.filter(x => x.count > 0)"
                  :key="it.key"
                  class="ha-task"
                  @click="go(it.route)"
                >
                  <v-icon small :color="sevColor(it.severity)">{{ it.icon || 'mdi-checkbox-blank-circle-outline' }}</v-icon>
                  <span class="ha-task-label">{{ it.label }}</span>
                  <span class="ha-task-amt" v-if="it.amount_display">${{ it.amount_display }}</span>
                  <v-chip x-small label :color="sevColor(it.severity)" text-color="white">{{ it.count_display }}</v-chip>
                </div>
                <div v-if="!m.card.items.some(x => x.count > 0)" class="ha-empty">Nothing pending — all caught up 🎉</div>
              </div>

              <!-- list card (client sub-resource / top transactions / module overview) -->
              <div v-else-if="m.card && m.card.card_type === 'list'" class="ha-card">
                <div class="ha-list-head">
                  <span class="ha-list-title">{{ m.card.title }}</span>
                  <span class="ha-list-sub" v-if="m.card.subtitle">{{ m.card.subtitle }}</span>
                  <span class="ha-list-overview" v-if="m.card.overview" v-html="render(m.card.overview)" @click="onTextClick"></span>
                </div>
                <div v-if="!m.card.rows || !m.card.rows.length" class="ha-empty">{{ m.card.empty || 'Nothing on record.' }}</div>
                <div v-for="(r, ri) in (m.card.rows || [])" :key="ri" class="ha-list-row">
                  <div class="ha-list-main">
                    <div class="ha-list-primary">{{ r.title }}</div>
                    <div class="ha-list-secondary" v-if="r.sub">{{ r.sub }}</div>
                  </div>
                  <div class="ha-list-right" v-if="r.right" :class="{ neg: String(r.right).indexOf('-') === 0 }">{{ r.right }}</div>
                </div>
                <div class="ha-list-foot" v-if="m.card.footer" v-html="render(m.card.footer)" @click="onTextClick"></div>
                <button v-if="m.card.deep_link" type="button" class="ha-cta" @click="go(m.card.deep_link)">
                  {{ m.card.cta || 'Open client' }} <span class="ha-cta-arrow">→</span>
                </button>
              </div>

              <!-- export / report card -->
              <div v-else-if="m.card && m.card.card_type === 'export'" class="ha-card ha-export">
                <div class="ha-list-head">
                  <span class="ha-list-title">{{ m.card.title || 'Report' }}</span>
                  <span class="ha-list-sub" v-if="m.card.subtitle">{{ m.card.subtitle }}</span>
                </div>
                <div v-if="m.card.dataset" class="ha-export-btns">
                  <button type="button" class="ha-dl" :disabled="m.card._busy" @click="downloadExport(m.card, 'csv')">
                    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v10m0 0l-4-4m4 4l4-4M5 19h14"/></svg>
                    CSV
                  </button>
                  <button type="button" class="ha-dl" :disabled="m.card._busy" @click="downloadExport(m.card, 'xlsx')">
                    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v10m0 0l-4-4m4 4l4-4M5 19h14"/></svg>
                    Excel
                  </button>
                </div>
                <div v-else class="ha-export-hint">Try: "download deposit report this month" · "export clients as excel".</div>
                <button v-if="m.card.deep_link" type="button" class="ha-cta" @click="go(m.card.deep_link)">
                  {{ m.card.cta || 'Open page' }} <span class="ha-cta-arrow">→</span>
                </button>
              </div>

              <!-- client 360 card -->
              <div v-else-if="m.card && m.card.card_type === 'client360'" class="ha-card">
                <div class="ha-c360-row"><b>#{{ m.card.profile.id }}</b> · {{ m.card.profile.ib_status }} · {{ m.card.profile.country || '—' }}</div>
                <div class="ha-c360-grid">
                  <div><span>Trading a/c</span><b>{{ m.card.trading_accounts_summary.total }}</b></div>
                  <div><span>Live / Demo</span><b>{{ m.card.trading_accounts_summary.live }} / {{ m.card.trading_accounts_summary.demo }}</b></div>
                  <div><span>Balance</span><b>${{ m.card.trading_accounts_summary.total_balance }}</b></div>
                  <div><span>Logins</span><b>{{ m.card.logins.count }}</b></div>
                  <div><span>Deposits</span><b>${{ money(m.card.transactions.deposit.approved_amount) }}</b></div>
                  <div><span>KYC</span><b>{{ m.card.kyc.identity }}</b></div>
                </div>
                <div v-if="m.card.flags && m.card.flags.length" class="ha-flags">
                  <v-icon x-small color="orange">mdi-alert</v-icon> {{ m.card.flags.join(' · ') }}
                </div>
                <button type="button" class="ha-cta" @click="go(m.card.deep_links && m.card.deep_links.profile)">
                  Open client <span class="ha-cta-arrow">→</span>
                </button>
              </div>

              <!-- clarify card -->
              <div v-else-if="m.card && m.card.card_type === 'clarify'" class="ha-card">
                <v-btn
                  v-for="o in m.card.options"
                  :key="o.id"
                  x-small outlined class="ha-clarify"
                  @click="send('client ' + o.id)"
                >{{ o.name || o.email }} · #{{ o.id }}</v-btn>
              </div>

              <!-- howto page link(s) -->
              <div v-else-if="m.card && m.card.card_type === 'howto' && (m.card.page_routes || []).length" class="ha-card">
                <button
                  v-for="(pr, pi) in m.card.page_routes.slice(0, 2)"
                  :key="pi"
                  type="button" class="ha-cta"
                  @click="go(pr)"
                >
                  {{ routeLabel(pr) }} <span class="ha-cta-arrow">→</span>
                </button>
              </div>

              <!-- capabilities chips -->
              <div v-else-if="m.card && m.card.card_type === 'capabilities'" class="ha-card ha-chips">
                <v-chip v-for="(s, si) in m.card.samples" :key="si" x-small class="ha-chip" @click="send(s)">{{ s }}</v-chip>
              </div>

              <div v-if="m.role === 'bot' && m.id" class="ha-fb">
                <v-btn icon x-small @click="feedback(m, 'up')" :color="m.fb === 'up' ? 'green' : ''"><v-icon x-small>mdi-thumb-up-outline</v-icon></v-btn>
                <v-btn icon x-small @click="feedback(m, 'down')" :color="m.fb === 'down' ? 'red' : ''"><v-icon x-small>mdi-thumb-down-outline</v-icon></v-btn>
              </div>
            </div>
          </div>

          <div v-if="loading" class="ha-msg ha-msg-bot">
            <div class="ha-bubble ha-typing"><span></span><span></span><span></span></div>
          </div>
        </div>

        <div class="ha-sugg" v-if="suggestions.length && !loading" :class="{ 'is-collapsed': !suggOpen }">
          <button type="button" class="ha-sugg-label" @click="toggleSugg" :aria-expanded="suggOpen ? 'true' : 'false'">
            <span>Try asking</span>
            <svg class="ha-sugg-caret" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path fill="currentColor" d="M7 10l5 5 5-5z" />
            </svg>
          </button>
          <div class="ha-sugg-row" v-show="suggOpen">
            <button
              v-for="(s, i) in suggestions"
              :key="i"
              type="button" class="ha-chip"
              @click="send(s.prompt)"
            >
              <span class="ha-chip-dot">✦</span>{{ s.label }}
            </button>
          </div>
        </div>

        <div class="ha-input">
          <div class="ha-input-wrap" :class="{ 'is-active': draft.trim().length, 'is-listening': listening }">
            <button
              v-if="ttsSupported"
              type="button" class="ha-icon-btn ha-speak"
              :class="{ 'is-on': speakReplies }"
              :title="speakReplies ? 'Voice replies: on' : 'Voice replies: off'"
              @click="toggleSpeak"
            >
              <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
                <path fill="currentColor" d="M3 10v4h4l5 5V5L7 10H3z"/>
                <path v-if="speakReplies" fill="currentColor" d="M16.5 12a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zm-2.5-9v2.06a7 7 0 0 1 0 13.88V21a9 9 0 0 0 0-18z"/>
                <path v-else fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M16 9l5 6M21 9l-5 6"/>
              </svg>
            </button>
            <input
              ref="composer"
              v-model="draft"
              type="text"
              class="ha-input-field"
              :placeholder="listening ? 'Listening…' : 'Ask me anything…'"
              autocomplete="off"
              @keyup.enter="send()"
              @keydown.up.prevent="historyPrev"
              @keydown.down.prevent="historyNext"
              @input="onComposerInput"
            />
            <button
              v-if="sttSupported"
              type="button" class="ha-icon-btn ha-mic"
              :class="{ 'is-listening': listening }"
              :disabled="loading"
              :title="listening ? 'Stop' : 'Speak your question'"
              @click="toggleMic"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path fill="currentColor" d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z"/>
                <path fill="currentColor" d="M19 12a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.92V21H8a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2h-3v-2.08A7 7 0 0 0 19 12z"/>
              </svg>
            </button>
            <button
              type="button" class="ha-send"
              :disabled="loading || !draft.trim()"
              @click="send()"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
            </button>
          </div>
        </div>
      </v-card>
    </transition>

    <!-- Floating avatar (draggable, Messenger-head style) -->
    <div
      class="ha-fab-wrap"
      ref="fab"
      :class="{ 'is-busy': loading, 'is-open': open, 'is-dragging': fab.dragging }"
      @pointerdown="onFabDown"
      @pointermove="onFabMove"
      @pointerup="onFabUp"
      @pointercancel="onFabUp"
      @lostpointercapture="onFabUp"
    >
      <span v-if="!open" class="ha-fab-aura" aria-hidden="true"></span>
      <span v-if="!open" class="ha-fab-aura ha-fab-aura--2" aria-hidden="true"></span>
      <v-btn fab color="#1e88e5" class="ha-fab" @click="toggle" :title="assistantName">
        <span v-if="!open" class="ha-ai-ico" v-html="aiIcon"></span>
        <v-icon v-else>mdi-close</v-icon>
        <span v-if="!open && unread" class="ha-dot"></span>
      </v-btn>
    </div>
  </div>
</template>

<script>
import HTTP from "@/_helpers/http";

export default {
  name: "HelpAssistantWidget",
  data() {
    return {
      active: false,
      open: false,
      loading: false,
      unread: false,
      assistantName: "Help Assistant",
      adminName: null,
      draft: "",
      // --- voice ---
      sttSupported: false, // speech-to-text (mic input)
      ttsSupported: false, // text-to-speech (spoken replies)
      listening: false,
      speakReplies: false,
      voiceGender: "female", // from bot_settings.assistant_voice (female | male)
      conversationId: null,
      messages: [],
      suggestions: [],
      bootTried: false,
      history: [],
      histIndex: -1,
      histStash: "",
      suggOpen: true,
      // draggable floating avatar (null x = default bottom-right via CSS)
      fab: { x: null, y: null, side: "right", dragging: false },
      viewport: {
        w: typeof window !== "undefined" ? window.innerWidth : 1280,
        h: typeof window !== "undefined" ? window.innerHeight : 800,
      },
      aiIcon:
        '<svg viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<g stroke="currentColor" stroke-width="30" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M96 214V166a160 160 0 0 1 320 0v202"/>' +
        '<rect x="14" y="196" width="86" height="150" rx="26"/>' +
        '<rect x="412" y="196" width="86" height="150" rx="26"/>' +
        '<path d="M96 346v42a96 96 0 0 0 96 96h22"/>' +
        '<rect x="214" y="458" width="94" height="50" rx="25"/>' +
        '<path d="M198 300l38-116 38 116"/>' +
        '<path d="M212 262h48"/>' +
        '<path d="M322 184v116"/>' +
        '</g></svg>',
    };
  },
  computed: {
    dark() {
      return this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark;
    },
    hasToken() {
      try {
        return !!(this.$store && this.$store.state.authentication && this.$store.state.authentication.token);
      } catch (e) {
        return !!localStorage.getItem("token");
      }
    },
    onAuthPage() {
      return this.$route && (this.$route.meta.noAuth || this.$route.meta.noSidebar || this.$route.path === "/");
    },
    visible() {
      return this.active && this.hasToken && !this.onAuthPage;
    },
    routeKey() {
      return (this.$route && this.$route.path ? this.$route.path : "").replace(/^\/+/, "");
    },
    rootStyle() {
      if (this.fab.x == null) return {};
      return {
        left: this.fab.x + "px",
        top: this.fab.y + "px",
        right: "auto",
        bottom: "auto",
      };
    },
    panelStyle() {
      if (this.fab.x == null) return {};
      const s = {};
      if (this.fab.side === "left") {
        s.left = "0";
        s.right = "auto";
      } else {
        s.right = "0";
        s.left = "auto";
      }
      // open downward when the avatar sits in the top ~45% of the screen
      if (this.fab.y < this.viewport.h * 0.45) {
        s.top = "68px";
        s.bottom = "auto";
      } else {
        s.bottom = "68px";
        s.top = "auto";
      }
      return s;
    },
  },
  watch: {
    hasToken(v) {
      if (v && !this.bootTried) this.boot();
    },
    routeKey() {
      if (this.active) this.loadSuggestions();
    },
    open(v) {
      if (v) {
        this.unread = false;
        if (!this.messages.length) this.greet();
        this.$nextTick(() => {
          this.scrollBottom();
          if (this.$refs.composer) this.$refs.composer.focus();
        });
      } else {
        this.stopListening();
        this.stopSpeaking();
      }
    },
  },
  mounted() {
    this.loadHistory();
    try {
      const v = localStorage.getItem("ha_sugg_open");
      if (v !== null) this.suggOpen = v === "1";
      this.speakReplies = localStorage.getItem("ha_tts") === "1";
    } catch (e) {
      /* ignore */
    }
    this.initVoice();
    this.loadFabPos();
    window.addEventListener("resize", this.onWinResize);
    if (this.hasToken) this.boot();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onWinResize);
    try { window.speechSynthesis.removeEventListener("voiceschanged", this.pickVoice); } catch (e) { /* ignore */ }
    this.stopListening();
    this.stopSpeaking();
  },
  methods: {
    toggleSugg() {
      this.suggOpen = !this.suggOpen;
      try {
        localStorage.setItem("ha_sugg_open", this.suggOpen ? "1" : "0");
      } catch (e) {
        /* ignore */
      }
    },
    async boot() {
      this.bootTried = true;
      try {
        const { data } = await HTTP().get("/assistant/config", { params: { route: this.routeKey } });
        if (!data || !data.active) {
          this.active = false;
          return;
        }
        this.active = true;
        this.assistantName = data.name || "Help Assistant";
        this.adminName = data.admin_name || null;
        this.suggestions = data.suggestions || [];
        this.voiceGender = data.voice === "male" ? "male" : "female";
        this._voice = null; // re-pick with the configured gender
        this.pickVoice();
      } catch (e) {
        this.active = false;
      }
    },
    async loadSuggestions() {
      try {
        const { data } = await HTTP().get("/assistant/suggestions", { params: { route: this.routeKey } });
        if (data && data.success) this.suggestions = data.suggestions || [];
      } catch (e) {
        /* ignore */
      }
    },
    toggle() {
      // swallow the click the browser fires right after a drag
      if (this._pointerHandled) {
        this._pointerHandled = false;
        return;
      }
      this.open = !this.open;
    },

    /* ---------------- draggable avatar (Messenger chat-head style) ---------- */
    clampFab(x, y) {
      const m = 10;
      const sz = 56;
      return {
        x: Math.max(m, Math.min(x, this.viewport.w - sz - m)),
        y: Math.max(m, Math.min(y, this.viewport.h - sz - m)),
      };
    },
    onFabDown(e) {
      if (this.open || (e.pointerType === "mouse" && e.button !== 0)) return;
      const r = this.$refs.fab.getBoundingClientRect();
      // don't capture the pointer yet — a plain tap must still fire a real
      // click on the button so the panel opens. We only capture once a drag
      // actually starts (in onFabMove).
      this._drag = {
        id: e.pointerId,
        sx: e.clientX,
        sy: e.clientY,
        offX: e.clientX - r.left,
        offY: e.clientY - r.top,
        moved: false,
      };
    },
    onFabMove(e) {
      if (!this._drag) return;
      const dx = e.clientX - this._drag.sx;
      const dy = e.clientY - this._drag.sy;
      if (!this._drag.moved) {
        if (Math.sqrt(dx * dx + dy * dy) < 6) return;
        this._drag.moved = true;
        this.fab.dragging = true;
        try {
          this.$refs.fab.setPointerCapture(this._drag.id);
        } catch (err) {
          /* ignore */
        }
      }
      const p = this.clampFab(e.clientX - this._drag.offX, e.clientY - this._drag.offY);
      this.fab.x = p.x;
      this.fab.y = p.y;
    },
    onFabUp(e) {
      if (!this._drag) return;
      const wasDrag = this._drag.moved;
      if (wasDrag) {
        try {
          this.$refs.fab.releasePointerCapture(this._drag.id);
        } catch (err) {
          /* ignore */
        }
        // block the synthetic click that follows the drag
        this._pointerHandled = true;
        setTimeout(() => {
          this._pointerHandled = false;
        }, 400);
        this.snapFab();
      }
      // a plain tap falls through: the real click event opens the panel
      this._drag = null;
      this.fab.dragging = false;
    },
    snapFab() {
      const m = 12;
      const sz = 56;
      const cx = this.fab.x + sz / 2;
      const side = cx < this.viewport.w / 2 ? "left" : "right";
      this.fab.side = side;
      this.fab.x = side === "left" ? m : this.viewport.w - sz - m;
      this.fab.y = Math.max(m, Math.min(this.fab.y, this.viewport.h - sz - m));
      this.saveFabPos();
    },
    saveFabPos() {
      try {
        localStorage.setItem(
          "ha_fab_pos",
          JSON.stringify({ x: this.fab.x, y: this.fab.y, side: this.fab.side })
        );
      } catch (e) {
        /* ignore */
      }
    },
    loadFabPos() {
      try {
        const raw = localStorage.getItem("ha_fab_pos");
        if (!raw) return;
        const p = JSON.parse(raw);
        if (typeof p.x !== "number" || typeof p.y !== "number") return;
        const c = this.clampFab(p.x, p.y);
        this.fab.x = c.x;
        this.fab.y = c.y;
        this.fab.side = p.side === "left" ? "left" : "right";
      } catch (e) {
        /* ignore */
      }
    },
    onWinResize() {
      this.viewport = { w: window.innerWidth, h: window.innerHeight };
      if (this.fab.x != null) {
        if (this.fab.side === "left") this.fab.x = 12;
        else this.fab.x = this.viewport.w - 56 - 12;
        const c = this.clampFab(this.fab.x, this.fab.y);
        this.fab.x = c.x;
        this.fab.y = c.y;
      }
    },
    greet() {
      const hour = new Date().getHours();
      const part = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : hour < 21 ? "Good evening" : "Hello";
      this.messages.push({
        role: "bot",
        text: `${part}${this.adminName ? ", " + this.adminName : ""} 👋  I can pull live numbers, look up a client, list what's pending today, or walk you through a task. Try a suggestion below.`,
        card: null,
      });
    },
    resetChat() {
      this.messages = [];
      this.conversationId = null;
      this.greet();
      this.loadSuggestions();
    },
    loadHistory() {
      try {
        const raw = localStorage.getItem("ha_hist");
        this.history = raw ? JSON.parse(raw).slice(-50) : [];
      } catch (e) {
        this.history = [];
      }
    },
    pushHistory(msg) {
      if (this.history[this.history.length - 1] === msg) return;
      this.history.push(msg);
      if (this.history.length > 50) this.history = this.history.slice(-50);
      try {
        localStorage.setItem("ha_hist", JSON.stringify(this.history));
      } catch (e) {
        /* ignore */
      }
    },
    historyPrev() {
      if (!this.history.length) return;
      if (this.histIndex === -1) {
        this.histStash = this.draft;
        this.histIndex = this.history.length - 1;
      } else if (this.histIndex > 0) {
        this.histIndex -= 1;
      }
      this.draft = this.history[this.histIndex];
      this.$nextTick(this.caretToEnd);
    },
    historyNext() {
      if (this.histIndex === -1) return;
      if (this.histIndex < this.history.length - 1) {
        this.histIndex += 1;
        this.draft = this.history[this.histIndex];
      } else {
        this.histIndex = -1;
        this.draft = this.histStash;
      }
      this.$nextTick(this.caretToEnd);
    },
    caretToEnd() {
      const el = this.$refs.composer;
      if (el) el.setSelectionRange(el.value.length, el.value.length);
    },

    /* ------------------------------- voice ------------------------------- */
    initVoice() {
      // text-to-speech (spoken replies) — universal
      this.ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;
      if (this.ttsSupported) {
        this.pickVoice();
        // voice list loads async in Chrome
        try { window.speechSynthesis.addEventListener("voiceschanged", this.pickVoice); } catch (e) { /* ignore */ }
      }
      // speech-to-text (mic input) — Chrome / Edge (webkit-prefixed)
      const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
      if (!SR) return;
      try {
        const r = new SR();
        r.lang = "en-US";
        r.interimResults = true;
        r.continuous = false;
        r.maxAlternatives = 1;
        r.onresult = (e) => {
          let finalTxt = "";
          let interim = "";
          for (let i = e.resultIndex; i < e.results.length; i++) {
            const t = e.results[i][0].transcript;
            if (e.results[i].isFinal) finalTxt += t;
            else interim += t;
          }
          this.draft = (finalTxt || interim).replace(/\s+/g, " ").trimStart();
          if (finalTxt.trim()) {
            this._voiceFinal = true;
            try { r.stop(); } catch (err) { /* ignore */ }
          }
        };
        r.onend = () => {
          this.listening = false;
          if (this._voiceFinal && this.draft.trim() && !this.loading) {
            this._voiceFinal = false;
            this.send();
          }
        };
        r.onerror = (e) => {
          this.listening = false;
          this._voiceFinal = false;
          if (e.error === "not-allowed" || e.error === "service-not-allowed") {
            this.messages.push({
              role: "bot",
              text: "I couldn't access the microphone. Allow mic permission for this site (and make sure it's opened over HTTPS), then try again.",
              card: null,
            });
            this.$nextTick(this.scrollBottom);
          }
        };
        this._recog = r;
        this.sttSupported = true;
      } catch (e) {
        this.sttSupported = false;
      }
    },
    toggleMic() {
      if (!this._recog || this.loading) return;
      if (this.listening) {
        this.stopListening();
        return;
      }
      this.stopSpeaking();
      this.draft = "";
      this._voiceFinal = false;
      try {
        this._recog.start();
        this.listening = true;
      } catch (e) {
        this.listening = false;
      }
    },
    stopListening() {
      if (this._recog && this.listening) {
        this._voiceFinal = false;
        try { this._recog.stop(); } catch (e) { /* ignore */ }
      }
      this.listening = false;
    },
    onComposerInput() {
      this.histIndex = -1;
      if (this.listening) this.stopListening();
    },
    toggleSpeak() {
      this.speakReplies = !this.speakReplies;
      try { localStorage.setItem("ha_tts", this.speakReplies ? "1" : "0"); } catch (e) { /* ignore */ }
      if (!this.speakReplies) this.stopSpeaking();
    },
    // browsers don't expose voice gender — match on known voice names
    pickVoice() {
      if (!this.ttsSupported) return;
      let voices = [];
      try { voices = window.speechSynthesis.getVoices() || []; } catch (e) { return; }
      if (!voices.length) return;
      const en = voices.filter((v) => /^en[-_]?/i.test(v.lang || ""));
      const pool = en.length ? en : voices;
      const FEM = /(female|zira|samantha|karen|moira|tessa|fiona|victoria|susan|allison|serena|aria|jenny|michelle|hazel|catherine|linda|heera|sabina|\bava\b|\bemma\b|\bnora\b|google us english$)/i;
      const MAL = /(\bmale\b|david|mark|daniel|oliver|arthur|rishi|ravi|george|james|\balex\b|\bfred\b|\btom\b|\bguy\b|google uk english male)/i;
      const want = this.voiceGender === "male" ? MAL : FEM;
      const avoid = this.voiceGender === "male" ? FEM : MAL;
      this._voice =
        pool.find((v) => want.test(v.name || "")) ||
        pool.find((v) => !avoid.test(v.name || "")) ||
        pool[0] || null;
    },
    speak(text) {
      if (!this.speakReplies || !this.ttsSupported || !text) return;
      const plain = String(text)
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // [label](route) → label
        .replace(/[*_`#>]/g, "")
        .replace(/\s+/g, " ")
        .trim();
      if (!plain) return;
      try {
        if (!this._voice) this.pickVoice();
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(plain.slice(0, 600));
        if (this._voice) u.voice = this._voice;
        u.lang = (this._voice && this._voice.lang) || "en-US";
        u.rate = 1.02;
        // nudge pitch toward the configured gender when no gendered voice was found
        u.pitch = this.voiceGender === "male" ? 0.9 : 1.15;
        window.speechSynthesis.speak(u);
      } catch (e) {
        /* ignore */
      }
    },
    stopSpeaking() {
      if (this.ttsSupported) {
        try { window.speechSynthesis.cancel(); } catch (e) { /* ignore */ }
      }
    },

    async downloadExport(card, format) {
      if (!card || !card.dataset || card._busy) return;
      this.$set(card, "_busy", true);
      try {
        const res = await HTTP().get("/assistant/export", {
          params: {
            dataset: card.dataset,
            format,
            period: card.period || "",
            from: card.from || "",
            to: card.to || "",
          },
          responseType: "blob",
        });
        const cd = (res.headers && res.headers["content-disposition"]) || "";
        const nameM = cd.match(/filename="?([^"]+)"?/);
        const fname =
          (nameM && nameM[1]) ||
          `${card.dataset}.${format === "xlsx" ? "xlsx" : "csv"}`;
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      } catch (e) {
        this.messages.push({
          role: "bot",
          text: "Sorry — that export failed. Try a smaller date range.",
          card: null,
        });
        this.$nextTick(this.scrollBottom);
      } finally {
        this.$set(card, "_busy", false);
      }
    },

    async send(text) {
      const msg = (typeof text === "string" ? text : this.draft).trim();
      if (!msg || this.loading) return;
      this.stopListening();
      this.draft = "";
      this.histIndex = -1;
      this.histStash = "";
      this.pushHistory(msg);
      this.messages.push({ role: "user", text: msg });
      this.loading = true;
      this.$nextTick(this.scrollBottom);
      try {
        const { data } = await HTTP().post("/assistant/ask", {
          message: msg,
          conversation_id: this.conversationId,
          page_route: this.routeKey,
        });
        if (data && data.disabled) {
          this.active = false;
          return;
        }
        this.conversationId = data.conversation_id || this.conversationId;
        const reply = (data && data.reply) || {};
        this.messages.push({
          role: "bot",
          text: reply.text || "…",
          card: reply.card || null,
          id: data.message_id || null,
          fb: null,
        });
        this.speak(reply.text);
        if (!this.open) this.unread = true;
      } catch (e) {
        this.messages.push({ role: "bot", text: "Sorry — I couldn't reach the server. Please try again.", card: null });
      } finally {
        this.loading = false;
        this.$nextTick(this.scrollBottom);
      }
    },
    async feedback(m, rating) {
      if (!m.id) return;
      m.fb = rating;
      try {
        await HTTP().post("/assistant/feedback", { message_id: m.id, rating });
      } catch (e) {
        /* ignore */
      }
    },
    go(route) {
      if (!route) return;
      route = String(route).trim();
      if (route[0] !== "/") route = "/" + route.replace(/^\/+/, "");
      this.open = false;
      const [path, query] = route.split("?");
      const target = query
        ? { path, query: this.parseQuery(query) }
        : { path };
      if (this.$route.fullPath !== route) this.$router.push(target).catch(() => {});
    },
    parseQuery(qs) {
      const o = {};
      String(qs || "").split("&").forEach((p) => {
        const [k, v] = p.split("=");
        if (k) o[decodeURIComponent(k)] = decodeURIComponent(v || "");
      });
      return o;
    },
    onTextClick(e) {
      const a = e.target.closest && e.target.closest("a.ha-link");
      if (a) {
        e.preventDefault();
        this.go(a.getAttribute("data-route"));
      }
    },
    routeLabel(route) {
      const map = {
        "/create-new-user-account": "Open Add New User",
        "/registered-user": "Open Registered Users",
        "/registered-user?tab=1": "Open Registered Users",
        "/registered-user?tab=2": "Open Documents",
        "/registered-user?tab=3": "Open Live Trading Accounts",
        "/registered-user?tab=4": "Open Demo Accounts",
        "/registered-user?tab=5": "Open Blocked Users",
        "/registered-user?tab=6": "Open Inactive Accounts",
        "/registered-user?tab=7": "Open Low Balance Accounts",
        "/registered-user?tab=8": "Open Testing Accounts",
        "/registered-user?tab=9": "Open Swap Free Accounts",
        "/user-activity": "Open User Activity",
        "/deposit": "Open Deposits",
        "/withdraw": "Open Withdrawals",
        "/withdraw-requests": "Open Withdraw Requests",
        "/credit-in": "Open Credit In",
        "/internal-transctions": "Open Internal Transfers",
        "/partner-list": "Open Partner List",
        "/commission-settings": "Open Commission Settings",
        "/support": "Open Support Tickets",
        "/prop-trading-requests": "Open Prop Requests",
      };
      const r = route[0] === "/" ? route : "/" + route;
      if (map[r]) return map[r];
      const seg = r.split("?")[0].replace(/^\//, "").replace(/-/g, " ");
      return "Open " + seg.replace(/\b\w/g, (c) => c.toUpperCase());
    },
    scrollBottom() {
      const el = this.$refs.scroll;
      if (el) el.scrollTop = el.scrollHeight;
    },
    sevColor(s) {
      return s === "high" ? "red" : s === "medium" ? "orange" : "blue-grey";
    },
    money(n) {
      return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    render(text) {
      let s = String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      // markdown link [label](/route)
      s = s.replace(/\[([^\]]+)\]\((\/[^)\s]+)\)/g, (m, label, route) => {
        return `<a href="#" class="ha-link" data-route="${route}">${label}</a>`;
      });
      // inline `code` — turn a route-looking code span into a link, keep the rest as <code>
      s = s.replace(/`([^`]+)`/g, (m, inner) => {
        const t = inner.trim();
        if (/^\/[a-z0-9][a-z0-9/?=&_-]*$/i.test(t)) {
          return `<a href="#" class="ha-link" data-route="${t}">${t}</a>`;
        }
        if (/^\?tab=\d+$/i.test(t)) {
          return `<a href="#" class="ha-link" data-route="/registered-user${t}">${t}</a>`;
        }
        return `<code>${inner}</code>`;
      });
      return s
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/(^|[^_])_([^_]+)_(?!_)/g, "$1<em>$2</em>")
        .replace(/^\s*[-*]\s+(.*)$/gm, "• $1")
        .replace(/\n/g, "<br>");
    },
  },
};
</script>

<style scoped>
.help-assistant {
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 1000;
  font-family: inherit;
  transition: left 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    top 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}
.help-assistant.ha-dragging {
  transition: none;
}
/* ---- Siri-style animated floating avatar ---- */
.ha-fab-wrap {
  position: relative;
  width: 56px;
  height: 56px;
  display: flex;
  cursor: pointer; /* it's clickable; drag is a bonus */
  touch-action: none; /* let us handle drag without the page scrolling */
  user-select: none;
  -webkit-user-select: none;
  transition: transform 0.18s ease;
}
.ha-fab-wrap.is-dragging,
.ha-fab-wrap.is-dragging * {
  cursor: grabbing;
}
.ha-fab-wrap.is-dragging {
  transform: scale(1.08);
}
.ha-fab-wrap.is-dragging .ha-fab {
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.38) !important;
}
.ha-fab {
  position: relative;
  z-index: 2;
  background-color: #1e88e5 !important; /* same blue in light & dark */
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.28) !important;
  animation: ha-fab-breathe 4.2s ease-in-out infinite;
}
.ha-fab::v-deep .v-icon {
  color: #ffffff !important;
}
/* rotating iridescent halo behind the button */
.ha-fab-aura {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 78px;
  height: 78px;
  margin: -39px 0 0 -39px;
  border-radius: 50%;
  z-index: 1;
  pointer-events: none;
  background: conic-gradient(
    from 0deg,
    #1e88e5,
    #22d3ee,
    #7c4dff,
    #ec4899,
    #1e88e5
  );
  filter: blur(11px);
  opacity: 0.55;
  animation: ha-aura-spin 7s linear infinite;
  will-change: transform;
}
.ha-fab-aura--2 {
  width: 66px;
  height: 66px;
  margin: -33px 0 0 -33px;
  background: conic-gradient(from 180deg, #38bdf8, #a78bfa, #f472b6, #38bdf8);
  filter: blur(6px);
  opacity: 0.5;
  animation: ha-aura-spin 4.5s linear infinite reverse, ha-aura-pulse 3s ease-in-out infinite;
}
/* busy = assistant is thinking → speed up + intensify, like Siri listening */
.ha-fab-wrap.is-busy .ha-fab-aura {
  animation-duration: 2.2s;
  opacity: 0.8;
  filter: blur(9px);
}
.ha-fab-wrap.is-busy .ha-fab-aura--2 {
  animation: ha-aura-spin 1.4s linear infinite reverse, ha-aura-pulse 1s ease-in-out infinite;
  opacity: 0.75;
}
.ha-fab-wrap.is-busy .ha-fab {
  animation-duration: 1.4s;
}
.ha-fab-wrap.is-busy .ha-ai-ico {
  animation: ha-ico-throb 1s ease-in-out infinite;
}
.ha-fab-wrap.is-open .ha-fab {
  animation: none;
}
@keyframes ha-aura-spin {
  to { transform: rotate(360deg); }
}
@keyframes ha-aura-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.18); }
}
@keyframes ha-fab-breathe {
  0%, 100% {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.28),
      0 0 0 0 rgba(56, 189, 248, 0.0);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.28),
      0 0 22px 4px rgba(124, 77, 255, 0.35);
    transform: scale(1.045);
  }
}
@keyframes ha-ico-throb {
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.12); opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .ha-fab,
  .ha-fab-aura,
  .ha-fab-aura--2,
  .ha-ai-ico {
    animation: none !important;
  }
  .ha-fab-aura { opacity: 0.4; }
}
.ha-ai-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff; /* white line-art — visible on the blue FAB in light & dark */
}
.ha-ai-ico::v-deep svg {
  width: 26px;
  height: 26px;
  display: block;
}
.ha-ai-ico--sm::v-deep svg {
  width: 20px;
  height: 20px;
}
.ha-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff5252;
  border: 2px solid #fff;
}
.ha-panel {
  position: absolute;
  right: 0;
  bottom: 74px;
  width: 460px;
  max-width: calc(100vw - 32px);
  height: 600px;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  border-radius: 16px !important;
  overflow: hidden;
}
.ha-dark .ha-panel {
  background: #0b1524;
  color: #e6edf5;
}
.ha-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 8px 10px 12px;
  background: linear-gradient(135deg, #1e88e5, #1565c0);
  color: #fff;
}
.ha-head-av {
  background: rgba(255, 255, 255, 0.18);
}
.ha-head-txt {
  line-height: 1.15;
  overflow: hidden;
}
.ha-title {
  font-weight: 700;
  font-size: 14px;
}
.ha-sub {
  font-size: 10.5px;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ha-head .v-btn {
  color: #fff !important;
}
.ha-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f4f7fb;
}
.ha-dark .ha-body {
  background: #0b1524;
}
.ha-msg {
  display: flex;
  margin-bottom: 10px;
}
.ha-msg-user {
  justify-content: flex-end;
}
.ha-bubble {
  max-width: 90%;
  padding: 9px 12px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  word-break: break-word;
}
.ha-msg-bot .ha-bubble {
  background: #fff;
  border-bottom-left-radius: 4px;
}
.ha-dark .ha-msg-bot .ha-bubble {
  background: #15243a;
  color: #e6edf5;
}
.ha-msg-user .ha-bubble {
  background: #1e88e5;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.ha-text::v-deep code {
  background: rgba(127, 127, 127, 0.16);
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 12px;
}
.ha-text::v-deep a.ha-link {
  color: #1565c0;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  word-break: break-word;
}
.ha-text::v-deep a.ha-link:hover {
  color: #0d47a1;
}
.ha-dark .ha-text::v-deep a.ha-link {
  color: #7ab8ff;
}
.ha-dark .ha-text::v-deep a.ha-link:hover {
  color: #a9d1ff;
}
.ha-card {
  margin-top: 8px;
  border-top: 1px dashed rgba(127, 127, 127, 0.3);
  padding-top: 8px;
}
.ha-export-btns {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
.ha-export-hint {
  margin-top: 8px;
  font-size: 11.5px;
  color: #7f8ca0;
}
.ha-dl {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  border: 1.5px solid #1e88e5;
  border-radius: 8px;
  background: rgba(30, 136, 229, 0.08);
  color: #1565c0;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.ha-dl:hover:not(:disabled) {
  background: #1e88e5;
  color: #fff;
}
.ha-dl:disabled {
  opacity: 0.55;
  cursor: default;
}
.ha-dark .ha-dl {
  color: #9fd0ff;
  background: rgba(74, 147, 236, 0.14);
  border-color: #4a93ec;
}
.ha-dark .ha-dl:hover:not(:disabled) {
  background: #4a93ec;
  color: #fff;
}
.ha-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  margin-right: 6px;
  padding: 6px 12px;
  border: 1px solid #1565c0;
  border-radius: 8px;
  background: #1565c0;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease;
}
.ha-cta:hover {
  background: #0d47a1;
  border-color: #0d47a1;
}
.ha-cta-arrow {
  font-size: 13px;
}
.ha-dark .ha-cta {
  background: #2f7fe0;
  border-color: #2f7fe0;
  color: #ffffff;
}
.ha-dark .ha-cta:hover {
  background: #4a93ec;
  border-color: #4a93ec;
}
.ha-task {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  cursor: pointer;
  font-size: 12.5px;
}
.ha-task:hover {
  opacity: 0.75;
}
.ha-task-label {
  flex: 1;
}
.ha-task-amt {
  font-size: 11px;
  opacity: 0.7;
}
.ha-empty {
  font-size: 12px;
  opacity: 0.7;
  padding: 4px 0;
}
.ha-list-head {
  display: flex;
  flex-direction: column;
  margin-bottom: 6px;
}
.ha-list-title {
  font-weight: 700;
  font-size: 12.5px;
}
.ha-list-sub {
  font-size: 10.5px;
  opacity: 0.6;
}
.ha-list-overview {
  font-size: 11px;
  opacity: 0.78;
  margin-top: 3px;
  line-height: 1.4;
}
.ha-list-foot {
  font-size: 10.5px;
  opacity: 0.55;
  margin-top: 8px;
  line-height: 1.4;
}
.ha-list-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 0;
  border-top: 1px solid rgba(127, 127, 127, 0.14);
  font-size: 12px;
}
.ha-list-main {
  flex: 1;
  min-width: 0;
}
.ha-list-primary {
  font-weight: 600;
  word-break: break-word;
}
.ha-list-secondary {
  font-size: 11px;
  opacity: 0.65;
  word-break: break-word;
}
.ha-list-right {
  font-weight: 700;
  color: #1b8f4c;
  white-space: nowrap;
}
.ha-list-right.neg {
  color: #d23b3b;
}
.ha-c360-row {
  font-size: 12px;
  margin-bottom: 6px;
}
.ha-c360-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 10px;
}
.ha-c360-grid > div {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}
.ha-c360-grid span {
  opacity: 0.65;
}
.ha-flags {
  margin-top: 6px;
  font-size: 11.5px;
  color: #e07b00;
}
.ha-clarify {
  margin: 3px 4px 0 0;
  text-transform: none;
}
.ha-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.ha-fb {
  margin-top: 2px;
  margin-left: -6px;
  opacity: 0.6;
}
.ha-sugg {
  padding: 10px 12px 4px;
  background: #f4f7fb;
}
.ha-sugg.is-collapsed {
  padding-bottom: 6px;
}
.ha-dark .ha-sugg {
  background: #0b1524;
}
.ha-sugg-label {
  display: flex;
  align-items: center;
  gap: 3px;
  width: 100%;
  padding: 2px 0;
  border: 0;
  background: transparent;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #90a4c8;
  margin-bottom: 6px;
  cursor: pointer;
}
.ha-sugg.is-collapsed .ha-sugg-label {
  margin-bottom: 0;
}
.ha-sugg-label:hover {
  color: #5b78a8;
}
.ha-sugg-caret {
  transition: transform 0.15s ease;
}
.ha-sugg.is-collapsed .ha-sugg-caret {
  transform: rotate(-90deg);
}
.ha-sugg-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ha-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: 1px solid #bcd7f5;
  border-radius: 999px;
  background: linear-gradient(180deg, #eef6ff, #dcecff);
  color: #12539c;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
}
.ha-chip:hover {
  background: linear-gradient(180deg, #1e88e5, #1565c0);
  border-color: #1565c0;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(21, 101, 192, 0.28);
}
.ha-chip-dot {
  font-size: 10px;
  opacity: 0.7;
}
.ha-dark .ha-chip {
  border-color: #2f4b6e;
  background: linear-gradient(180deg, #15263c, #10203a);
  color: #9fd0ff;
}
.ha-dark .ha-chip:hover {
  background: linear-gradient(180deg, #2f7fe0, #1e5fb0);
  border-color: #2f7fe0;
  color: #fff;
}

.ha-input {
  padding: 10px 12px 12px;
  background: #f4f7fb;
}
.ha-dark .ha-input {
  background: #0b1524;
}
.ha-input-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 4px 4px 6px;
  background: #ffffff;
  border: 1.5px solid #d5dfec;
  border-radius: 999px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ha-input-wrap:focus-within {
  border-color: #1e88e5;
  box-shadow: 0 0 0 4px rgba(30, 136, 229, 0.16);
}
.ha-input-wrap.is-active {
  border-color: #1e88e5;
}
.ha-dark .ha-input-wrap {
  background: #0f1a2a;
  border-color: #2c3f57;
}
.ha-dark .ha-input-wrap:focus-within {
  border-color: #4a93ec;
  box-shadow: 0 0 0 4px rgba(74, 147, 236, 0.2);
}
.ha-input-field {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: inherit;
  padding: 8px 4px 8px 6px;
}
.ha-input-field::placeholder {
  color: #9aa7b8;
}
.ha-send {
  flex: none;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: #1e88e5;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
}
.ha-send:hover:not(:disabled) {
  background: #1565c0;
}
.ha-send:active:not(:disabled) {
  transform: scale(0.92);
}
.ha-send:disabled {
  background: #c2d3e6;
  cursor: default;
}
.ha-dark .ha-send:disabled {
  background: #2c3f57;
}
/* ---- voice: mic + speaker toggle ---- */
.ha-icon-btn {
  flex: none;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #7f8ca0;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.1s ease;
}
.ha-icon-btn:hover:not(:disabled) {
  background: rgba(30, 136, 229, 0.12);
  color: #1e88e5;
}
.ha-icon-btn:active:not(:disabled) {
  transform: scale(0.9);
}
.ha-icon-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.ha-dark .ha-icon-btn {
  color: #8aa0bb;
}
.ha-speak.is-on {
  color: #1e88e5;
  background: rgba(30, 136, 229, 0.14);
}
.ha-mic.is-listening {
  color: #fff;
  background: #e53935;
  animation: ha-mic-pulse 1.4s ease-out infinite;
}
@keyframes ha-mic-pulse {
  0% { box-shadow: 0 0 0 0 rgba(229, 57, 53, 0.55); }
  70% { box-shadow: 0 0 0 9px rgba(229, 57, 53, 0); }
  100% { box-shadow: 0 0 0 0 rgba(229, 57, 53, 0); }
}
.ha-input-wrap.is-listening {
  border-color: #e53935;
  box-shadow: 0 0 0 4px rgba(229, 57, 53, 0.15);
}
@media (prefers-reduced-motion: reduce) {
  .ha-mic.is-listening { animation: none; }
}
.ha-typing span {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin: 0 2px;
  border-radius: 50%;
  background: #90a4ae;
  animation: ha-blink 1.2s infinite both;
}
.ha-typing span:nth-child(2) {
  animation-delay: 0.2s;
}
.ha-typing span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes ha-blink {
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
}
.ha-pop-enter-active,
.ha-pop-leave-active {
  transition: transform 0.16s ease, opacity 0.16s ease;
}
.ha-pop-enter,
.ha-pop-leave-to {
  transform: translateY(12px) scale(0.98);
  opacity: 0;
}
</style>
